<template>
  <Dialog
    v-model:visible="visible"
    header="⚙️ Управление магазинами"
    :modal="true"
    :style="{ width: '90vw', maxHeight: '90vh' }"
    @hide="onHide"
  >
    <div class="stores-management">
      <!-- Toolbar -->
      <div class="flex justify-content-between align-items-center mb-3">
        <div>
          <Button
            label="Создать магазин"
            icon="pi pi-plus"
            @click="showCreateDialog"
          />
        </div>
        <div class="text-sm text-color-secondary">
          Всего магазинов: {{ stores.length }}
        </div>
      </div>

      <!-- Таблица магазинов -->
      <DataTable
        :value="stores"
        :loading="isLoading"
        stripedRows
        size="small"
        scrollable
        scrollHeight="500px"
      >
        <Column header="Название" :style="{ width: '20%' }">
          <template #body="{ data }">
            <div class="flex align-items-center gap-2">
              <span>{{ getLocaleFlag(data.locale) }}</span>
              <span class="font-semibold">{{ data.name }}</span>
              <Tag v-if="data.is_default" value="По умолчанию" severity="success" size="small" />
              <Tag v-if="!data.is_active" value="Неактивен" severity="danger" size="small" />
            </div>
          </template>
        </Column>
        <Column field="description" header="Описание" :style="{ width: '25%' }"></Column>
        <Column header="Язык" :style="{ width: '15%' }">
          <template #body="{ data }">
            <div class="flex align-items-center gap-2">
              <span class="text-2xl">{{ getLocaleFlag(data.locale) }}</span>
              <span>{{ getLocaleName(data.locale) }}</span>
            </div>
          </template>
        </Column>
        <Column field="api_base_url" header="API URL" :style="{ width: '25%' }">
          <template #body="{ data }">
            <span class="text-xs text-color-secondary">{{ data.api_base_url }}</span>
          </template>
        </Column>
        <Column header="Действия" :style="{ width: '15%' }">
          <template #body="{ data }">
            <div class="flex gap-2">
              <!-- Кнопка переводов (только для неанглийских - EN базовый язык) -->
              <Button
                v-if="data.locale !== 'en'"
                icon="pi pi-language"
                severity="help"
                text
                v-tooltip.top="'Переводы'"
                @click="openTranslations(data)"
              />
              <!-- Редактировать -->
              <Button
                icon="pi pi-pencil"
                severity="info"
                text
                v-tooltip.top="'Редактировать'"
                @click="editStore(data)"
              />
              <!-- Удалить -->
              <Button
                icon="pi pi-trash"
                severity="danger"
                text
                v-tooltip.top="'Удалить'"
                @click="deleteStore(data)"
                :disabled="data.is_default"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Диалог создания/редактирования магазина -->
    <Dialog
      v-model:visible="showStoreForm"
      :header="editingStore ? 'Редактировать магазин' : 'Создать магазин'"
      :modal="true"
      :style="{ width: '600px' }"
    >
      <div class="flex flex-column gap-3">
        <!-- Название -->
        <div class="field">
          <label for="store-name">Название магазина *</label>
          <InputText
            id="store-name"
            v-model="storeForm.name"
            class="w-full"
            placeholder="Milano Store"
          />
        </div>

        <!-- Описание -->
        <div class="field">
          <label for="store-desc">Описание</label>
          <InputText
            id="store-desc"
            v-model="storeForm.description"
            class="w-full"
            placeholder="Магазин в Милане"
          />
        </div>

        <!-- Язык -->
        <div class="field">
          <label for="store-locale">Язык магазина *</label>
          <Dropdown
            id="store-locale"
            v-model="storeForm.locale"
            :options="availableLocales"
            optionLabel="name"
            optionValue="code"
            class="w-full"
            placeholder="Выберите язык"
          >
            <template #value="slotProps">
              <div v-if="slotProps.value" class="flex align-items-center gap-2">
                <span class="text-2xl">{{ getLocaleFlag(slotProps.value) }}</span>
                <span>{{ getLocaleName(slotProps.value) }}</span>
              </div>
              <span v-else>{{ slotProps.placeholder }}</span>
            </template>
            <template #option="slotProps">
              <div class="flex align-items-center gap-2">
                <span class="text-2xl">{{ slotProps.option.flag }}</span>
                <span>{{ slotProps.option.name }}</span>
              </div>
            </template>
          </Dropdown>
        </div>

        <!-- API URL -->
        <div class="field">
          <label for="store-url">API URL *</label>
          <InputText
            id="store-url"
            v-model="storeForm.api_base_url"
            class="w-full"
            placeholder="https://milano.parts-soft.ru/api/v3"
          />
        </div>

        <!-- API Username -->
        <div class="field">
          <label for="store-username">API Username *</label>
          <InputText
            id="store-username"
            v-model="storeForm.api_username"
            class="w-full"
            placeholder="username"
          />
        </div>

        <!-- API Password -->
        <div class="field">
          <label for="store-password">API Password *</label>
          <Password
            id="store-password"
            v-model="storeForm.api_password"
            class="w-full"
            :feedback="false"
            toggleMask
          />
        </div>

        <!-- Активность -->
        <div class="field-checkbox">
          <Checkbox
            id="store-active"
            v-model="storeForm.is_active"
            :binary="true"
          />
          <label for="store-active" class="ml-2">Магазин активен</label>
        </div>

        <!-- По умолчанию -->
        <div class="field-checkbox">
          <Checkbox
            id="store-default"
            v-model="storeForm.is_default"
            :binary="true"
          />
          <label for="store-default" class="ml-2">Магазин по умолчанию</label>
        </div>
      </div>

      <template #footer>
        <Button
          label="Отмена"
          icon="pi pi-times"
          @click="showStoreForm = false"
          severity="secondary"
        />
        <Button
          :label="editingStore ? 'Сохранить' : 'Создать'"
          icon="pi pi-check"
          @click="saveStore"
          :loading="isSaving"
        />
      </template>
    </Dialog>

    <!-- TranslationsDialog (вызывается из строки таблицы) -->
    <TranslationsDialog
      v-if="selectedStoreForTranslations"
      v-model="showTranslationsDialog"
      :store="selectedStoreForTranslations"
      @translations-updated="onTranslationsUpdated"
    />
  </Dialog>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Tag from 'primevue/tag'
import InputText from 'primevue/inputtext'
import Dropdown from 'primevue/dropdown'
import Password from 'primevue/password'
import Checkbox from 'primevue/checkbox'
import TranslationsDialog from './TranslationsDialog.vue'
import apiService from '@/services/apiService'
import { useToast } from 'primevue/usetoast'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'store-updated'])

const toast = useToast()

// Состояние
const visible = ref(props.modelValue)
const isLoading = ref(false)
const isSaving = ref(false)
const stores = ref([])
const showStoreForm = ref(false)
const editingStore = ref(null)
const availableLocales = ref([])

// Форма магазина
const storeForm = ref({
  name: '',
  description: '',
  locale: 'ru',
  api_base_url: '',
  api_username: '',
  api_password: '',
  is_active: true,
  is_default: false
})

// Переводы
const showTranslationsDialog = ref(false)
const selectedStoreForTranslations = ref(null)

// Синхронизация visible с modelValue
watch(() => props.modelValue, (val) => {
  visible.value = val
  if (val) {
    loadStores()
    loadLocales()
  }
})

watch(visible, (val) => {
  emit('update:modelValue', val)
})

// Методы
async function loadStores() {
  isLoading.value = true
  try {
    stores.value = await apiService.getStores()
  } catch (error) {
    console.error('Ошибка загрузки магазинов:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось загрузить список магазинов',
      life: 3000
    })
  } finally {
    isLoading.value = false
  }
}

async function loadLocales() {
  try {
    const locales = await apiService.getSupportedLocales()
    availableLocales.value = locales.map(l => ({
      code: l.code,
      name: l.name,
      flag: l.flag
    }))
  } catch (error) {
    console.error('Ошибка загрузки языков:', error)
  }
}

function showCreateDialog() {
  editingStore.value = null
  storeForm.value = {
    name: '',
    description: '',
    locale: 'ru',
    api_base_url: '',
    api_username: '',
    api_password: '',
    is_active: true,
    is_default: false
  }
  showStoreForm.value = true
}

function editStore(store) {
  editingStore.value = store
  storeForm.value = {
    name: store.name,
    description: store.description || '',
    locale: store.locale,
    api_base_url: store.api_base_url,
    api_username: store.api_username,
    api_password: '', // Не показываем пароль
    is_active: store.is_active,
    is_default: store.is_default
  }
  showStoreForm.value = true
}

async function saveStore() {
  // Валидация
  if (!storeForm.value.name || !storeForm.value.api_base_url ||
      !storeForm.value.api_username || (!editingStore.value && !storeForm.value.api_password)) {
    toast.add({
      severity: 'warn',
      summary: 'Проверьте данные',
      detail: 'Заполните все обязательные поля',
      life: 3000
    })
    return
  }

  isSaving.value = true
  try {
    if (editingStore.value) {
      // Обновление
      const updateData = { ...storeForm.value }
      if (!updateData.api_password) {
        delete updateData.api_password // Не обновляем пароль если не указан
      }

      await apiService.updateStore(editingStore.value.id, updateData)

      toast.add({
        severity: 'success',
        summary: 'Успешно',
        detail: 'Магазин обновлён',
        life: 3000
      })
    } else {
      // Создание
      await apiService.createStore(storeForm.value)

      toast.add({
        severity: 'success',
        summary: 'Успешно',
        detail: 'Магазин создан',
        life: 3000
      })
    }

    showStoreForm.value = false
    await loadStores()
    emit('store-updated')
  } catch (error) {
    console.error('Ошибка сохранения магазина:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error.response?.data?.detail || 'Не удалось сохранить магазин',
      life: 5000
    })
  } finally {
    isSaving.value = false
  }
}

async function deleteStore(store) {
  if (!confirm(`Удалить магазин "${store.name}"?`)) return

  try {
    await apiService.delete(`/stores/${store.id}`)

    toast.add({
      severity: 'success',
      summary: 'Удалено',
      detail: 'Магазин удалён',
      life: 3000
    })

    await loadStores()
    emit('store-updated')
  } catch (error) {
    console.error('Ошибка удаления магазина:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось удалить магазин',
      life: 3000
    })
  }
}

function openTranslations(store) {
  selectedStoreForTranslations.value = store
  showTranslationsDialog.value = true
}

function onTranslationsUpdated() {
  toast.add({
    severity: 'info',
    summary: 'Переводы обновлены',
    detail: 'Изменения сохранены',
    life: 3000
  })
}

function getLocaleFlag(locale) {
  const flags = {
    'ru': '🇷🇺',
    'en': '🇬🇧',
    'it': '🇮🇹',
    'de': '🇩🇪',
    'es': '🇪🇸'
  }
  return flags[locale] || '🌍'
}

function getLocaleName(locale) {
  const names = {
    'ru': 'Русский',
    'en': 'English',
    'it': 'Italiano',
    'de': 'Deutsch',
    'es': 'Español'
  }
  return names[locale] || locale
}

function onHide() {
  showStoreForm.value = false
  selectedStoreForTranslations.value = null
}

onMounted(() => {
  if (visible.value) {
    loadStores()
    loadLocales()
  }
})
</script>

<style scoped>
.stores-management {
  min-height: 400px;
}

.field {
  margin-bottom: 1rem;
}

.field label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.field-checkbox {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}
</style>
